import React, { useState, useEffect } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";

import { AuthProps, DefaultAndAuthProps, DefaultProps } from "model/props";
import { Admin } from "model/admin";
import { Account } from "model/account";

import css from "./app.module.scss";
import { readToken } from "misc/helpers";
import { Paths } from "appConfig";
import { useLoader } from "misc/loader";
import Loader from "components/loader/Loader";
import { retrieveAccessToken, signInWithAccessToken } from "misc/auth_utils";
import PasswordResetScreen from "screens/login/PasswordResetScreen";
import ResetPasswordPage from "screens/ResetPasswordPage";
import LoginScreen from "screens/login/LoginScreen";
import AccountScreen from "screens/account/AccountScreen";
import HomesScreen from "screens/homes/HomesScreen";
import { Home } from "model/home";
import UsersScreen from "screens/users/UsersScreen";
import Users2Screen from "screens/users/Users2Screen";
import StaffScreen from "screens/staff/StaffScreen";
import { StatsAndMergedSummary } from "model/stats";
import StatsScreen from "screens/stats/StatsScreen";
import { apiGetHomesList } from "net/requests";
import CalendarScreen from "screens/calendar/CalendarScreen";
import Calendar2Screen from "screens/calendar/Calendar2Screen";
import SideMenu from "components/sidemenu/SideMenu";
import OrganisationWrapper from "screens/organisation/OrganisationWrapper";
import AddOrganisationScreen from "screens/organisation/AddOrganisationScreen";
import SubOrganisationsScreen from "screens/sub-organisations/SubOrganisationsScreen";
import AddSubOrganisation from "screens/sub-organisations/AddSubOrganisation";
import EmployeesScreen from "screens/employees/EmployeesScreen";
import EditEmployeeScreen from "screens/employees/EditEmployeeScreen";
import AddUserScreen from "screens/users/AddUserScreen";
import EditUserScreen from "screens/users/EditUserScreen";
import AwardsScreen from "screens/awards/AwardsScreen";
import AwardWinnersScreen from "screens/awards/AwardWinnersScreen";
import HrStatisticsScreen from "screens/hrstatistics/HrStatisticsScreen";

import VerifyPage from "screens/VerifyPage";
import PrivacyPolicyPage from "screens/legal/PrivacyPolicyPage";
import TermsAndConditionsPage from "screens/legal/TermsAndConditionsPage";
import AdvanceYouPrivacyPolicyPage from "screens/legal/AdvanceYouPrivacyPolicyPage";
import AdvanceYouTermsAndConditionsPage from "screens/legal/AdvanceYouTermsAndConditionsPage";

const start_path = window.location.pathname;
const start_token = readToken();

function App() {
  ///////////////////////////////////////////////////////////////////////////

  const [admin, setAdmin] = useState<Admin>({ uuid: "", name: "" });
  const [account, setAccount] = useState<Account>();
  const [homes, setHomes] = useState<Home[]>();
  const [home, setHome] = useState<Home>();
  const [stats, setStats] = useState<StatsAndMergedSummary>();
  const [globalAppMode, setGlobalAppMode] = useState<"HEALTH" | "HR">("HEALTH");

  const [passwordResetToken, setPasswordResetToken] = useState<string>();

  const [invitationToken, setInvitationToken] = useState<string>();
  const resetInvitationToken = () => {
    setInvitationToken(undefined);
  };

  const [path, setPath] = useState<string>("/");

  const [loaderState, loader] = useLoader();

  const navigate = useNavigate();

  ///////////////////////////////////////////////////////////////////////////

  const defaultProps: DefaultProps = {
    path,
    loader,
    navigate,
    admin,
    setAdmin,
    globalAppMode,
    setGlobalAppMode,
    account,
    setAccount,
    home,
    setHome,
    homes,
    setHomes,
    stats,
    setStats,
  };

  const authProps: AuthProps = {
    onSignedOut: () => {},
    invitationToken,
    resetInvitationToken,
    passwordResetToken,
  };

  const defaultAndAuthProps: DefaultAndAuthProps = {
    ...defaultProps,
    ...authProps,
  };

  useEffect(() => {
    const path = start_path;
    const token = start_token;

    if (window.location.hostname !== "localhost")
      window.history.pushState("url-rewrite", document.title, path);

    switch (path) {
      case Paths.RESET_PASSWORD:
        setPasswordResetToken(token);
        return;
      default:
        break;
    }

    const accessToken = retrieveAccessToken();
    if (accessToken) {
      signInWithAccessToken(accessToken, defaultAndAuthProps);
      return;
    }

    // Allow public pages without auth.
    if (
      path === Paths.VERIFY ||
      path === Paths.RESET_PASSWORD ||
      path === Paths.PRIVACY_POLICY ||
      path === Paths.TERMS_AND_CONDITIONS ||
      path === Paths.INTEGRITETSPOLICY ||
      path === Paths.PRIVACY_POLICY_ADVANCE_YOU ||
      path === Paths.TERMS_AND_CONDITIONS_ADVANCE_YOU ||
      path === Paths.INTEGRITETSPOLICY_ADVANCE_YOU
    ) {
    } else {
      navigate(Paths.SIGNIN);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
  }, [admin]);

  useEffect(() => {
    if (admin?.access_token) {
      // Some roles may not have access to the /account endpoint, but they can
      // still have access to homes. Try to load homes even if account is unset.
      actionLoadHomes();
    }
    // eslint-disable-next-line
  }, [admin?.access_token]);

  useEffect(() => {
    setHome(undefined);
    setHomes(undefined);
    setStats(undefined);

    if (admin?.access_token) {
      actionLoadHomes();
    }
    // eslint-disable-next-line
  }, [account]);

  useEffect(() => {
    if (homes && homes.length) setHome(homes[0]);
    // eslint-disable-next-line
  }, [homes]);

  useEffect(() => {
    setPath(window.location.pathname);
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    // When the global app mode changes, reset the selected account
    // to the empty/initial state and clear related data.
    setAccount(undefined);
    setHome(undefined);
    setHomes(undefined);
    setStats(undefined);
    // eslint-disable-next-line
  }, [globalAppMode]);

  const actionLoadHomes = () => {
    apiGetHomesList(admin, account?.uuid)
      .then((response) => {
        const homes: Home[] = response.data;
        setHomes(homes);
      })
      .catch((error) => {
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  return (
    <div className={css.app}>
      {admin?.access_token && (
        <SideMenu {...defaultProps} {...authProps}></SideMenu>
      )}

      <div
        className={`${css.content} ${
          admin?.access_token ? css.contentWithMenu : ""
        }`}
      >
        <Routes>
          <Route
            path={Paths.SIGNIN}
            element={<LoginScreen {...defaultProps} {...authProps} />}
          />

          <Route path={Paths.RESET_PASSWORD} element={<ResetPasswordPage />} />
          <Route path={Paths.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
          <Route
            path={Paths.TERMS_AND_CONDITIONS}
            element={<TermsAndConditionsPage />}
          />
          <Route
            path={Paths.INTEGRITETSPOLICY}
            element={<PrivacyPolicyPage />}
          />
          <Route
            path={Paths.PRIVACY_POLICY_ADVANCE_YOU}
            element={<AdvanceYouPrivacyPolicyPage />}
          />
          <Route
            path={Paths.TERMS_AND_CONDITIONS_ADVANCE_YOU}
            element={<AdvanceYouTermsAndConditionsPage />}
          />
          <Route
            path={Paths.INTEGRITETSPOLICY_ADVANCE_YOU}
            element={<AdvanceYouPrivacyPolicyPage />}
          />
          <Route
            path={Paths.ORGANISATION}
            element={<OrganisationWrapper {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.ADD_ORGANISATION}
            element={<AddOrganisationScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.EDIT_ORGANISATION}
            element={<AddOrganisationScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.SUB_ORGANISATIONS}
            element={
              // Only auto-redirect operations/home managers (not superadmins)
              !admin?.is_super_admin &&
              (admin?.is_operations_manager || admin?.is_home_manager) &&
              homes &&
              homes.length > 0 ? (
                <Navigate
                  to={Paths.EDIT_SUB_ORGANISATIONS.replace(
                    ":id",
                    homes[0].uuid,
                  )}
                  replace
                />
              ) : (
                <SubOrganisationsScreen {...defaultProps} {...authProps} />
              )
            }
          />
          <Route
            path={Paths.ADD_SUB_ORGANISATIONS}
            element={<AddSubOrganisation {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.EDIT_SUB_ORGANISATIONS}
            element={<AddSubOrganisation {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.ACCOUNT}
            element={<AccountScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.HOMES}
            element={<HomesScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.STAFF}
            element={<StaffScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.USERS}
            element={<UsersScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.USERS2}
            element={<Users2Screen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.ADD_USERS2}
            element={<AddUserScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.EDIT_USERS2}
            element={<EditUserScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.EMPLOYEES}
            element={<EmployeesScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.AWARDS}
            element={<AwardsScreen {...defaultProps} {...authProps} />}
          />
          <Route
            path={Paths.AWARD_WINNERS}
            element={<AwardWinnersScreen {...defaultProps} {...authProps} />}
          />

          <Route
            path={Paths.CREATE_EMPLOYEES}
            element={<EditEmployeeScreen {...defaultProps} {...authProps} />}
          />

          <Route
            path={Paths.EDIT_EMPLOYEES}
            element={<EditEmployeeScreen {...defaultProps} {...authProps} />}
          />

          <Route
            path={Paths.CALENDAR_AGENDA}
            element={<CalendarScreen {...defaultProps} />}
          />
          <Route
            path={Paths.CALENDAR2}
            element={<Calendar2Screen {...defaultProps} />}
          />
          <Route
            path={Paths.STATISTICS}
            element={<StatsScreen {...defaultProps} />}
          />
          <Route
            path={Paths.HR_STATISTICS}
            element={<HrStatisticsScreen {...defaultProps} {...authProps} />}
          />
          <Route path={Paths.VERIFY} element={<VerifyPage />} />
          <Route path="*" element={<Navigate to={Paths.SIGNIN} />} />
        </Routes>
      </div>

      {loader.show && <Loader {...loaderState}></Loader>}
    </div>
  );
}

export default App;
